import { useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { toast } from "sonner";

export interface SpreadData {
  symbol: string;
  bid: number;
  ask: number;
  spread_points: number;
  digits: number;
  direction: "up" | "down" | "none";
  isOpen: boolean;
  change24h: string;
}

function parseNum(v: unknown): number {
  if (typeof v === "string" && v.includes("Closed")) return 0;
  if (v && typeof v === "object" && (v as any).isLosslessNumber)
    return Number((v as any).value);
  return typeof v === "number" ? v : Number(v) || 0;
}

function isMarketClosed(v: unknown): boolean {
  return typeof v === "string" && v.includes("Closed");
}

interface RawItem {
  symbol: string;
  common_suffix?: string;
  bid: unknown;
  ask: unknown;
  spread_points: unknown;
  digits: unknown;
  market_status?: string;
  "24h_changes"?: string;
}

function mapItem(
  item: RawItem,
  prevBids: Map<string, number>,
): SpreadData {
  const symbol = item.common_suffix ?? item.symbol;
  const closed = isMarketClosed(item.bid);
  const bid = parseNum(item.bid);
  const prevBid = prevBids.get(symbol);
  const direction: SpreadData["direction"] =
    closed || prevBid === undefined
      ? "none"
      : bid > prevBid
        ? "up"
        : bid < prevBid
          ? "down"
          : "none";
  if (!closed) prevBids.set(symbol, bid);

  return {
    symbol,
    bid,
    ask: parseNum(item.ask),
    spread_points: parseNum(item.spread_points),
    digits: parseNum(item.digits),
    direction,
    isOpen: !closed,
    change24h: item["24h_changes"] ?? "",
  };
}

const REST_URL = "https://rest.broker-radar.com";
const WS_URL = "wss://ws.broker-radar.com";

const ALL_SYMBOLS = [
  "EURUSD", "GBPUSD", "USDJPY", "USDCHF", "AUDUSD", "USDCAD", "NZDUSD",
  "XAUUSD", "XAGUSD", "BTCUSD", "ETHUSD", "WTIUSD",
  "USTEC", "NDXUSD", "SPXUSD", "US500",
].join(",");

class WebSocketManager {
  private static instance: WebSocketManager | null = null;
  private socket: Socket | null = null;
  private subscribers = new Set<(data: SpreadData[]) => void>();
  private connectionSubs = new Set<(connected: boolean) => void>();
  private isConnected = false;
  private currentData: SpreadData[] = [];
  private prevBids = new Map<string, number>();
  private refCount = 0;
  private restFetched = false;

  static getInstance() {
    if (!WebSocketManager.instance)
      WebSocketManager.instance = new WebSocketManager();
    return WebSocketManager.instance;
  }

  connect(broker: string, account: string) {
    this.refCount++;
    if (this.socket) return;

    // Fetch REST snapshot immediately for fast first paint
    if (!this.restFetched) {
      this.restFetched = true;
      fetch(`${REST_URL}/public/spreads/${broker}/${account}?symbols=${ALL_SYMBOLS}`)
        .then((r) => r.json())
        .then((json: { success: boolean; data: RawItem[] }) => {
          if (!json.success || !json.data?.length) return;
          // Only use REST data if WebSocket hasn't delivered yet
          if (this.currentData.length === 0) {
            this.currentData = json.data.map((item) =>
              mapItem(item, this.prevBids),
            );
            this.subscribers.forEach((cb) => cb(this.currentData));
          }
        })
        .catch(() => {});
    }

    this.socket = io(WS_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on("connect", () => {
      this.isConnected = true;
      this.notifyConnection();
      this.socket?.emit("authenticate", { mode: "public" });
    });

    this.socket.on("authenticated", (res: { success: boolean }) => {
      if (res.success) {
        this.socket?.emit("subscribe", { broker, account });
      }
    });

    this.socket.on("spreads", (response: { success: boolean; data?: RawItem[] }) => {
      if (!response.success || !response.data?.length) return;
      this.currentData = response.data.map((item) =>
        mapItem(item, this.prevBids),
      );
      this.subscribers.forEach((cb) => cb(this.currentData));
    });

    this.socket.on("connect_error", () => {
      this.isConnected = false;
      this.notifyConnection();
    });
    this.socket.on("disconnect", () => {
      this.isConnected = false;
      this.notifyConnection();
    });
  }

  disconnect() {
    if (--this.refCount <= 0 && this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.refCount = 0;
    }
  }

  subscribe(
    onData: (d: SpreadData[]) => void,
    onConn: (c: boolean) => void,
  ) {
    this.subscribers.add(onData);
    this.connectionSubs.add(onConn);
    onData(this.currentData);
    onConn(this.isConnected);
    return () => {
      this.subscribers.delete(onData);
      this.connectionSubs.delete(onConn);
    };
  }

  private notifyConnection() {
    this.connectionSubs.forEach((cb) => cb(this.isConnected));
  }
}

export function useLiveFeed(
  broker = "opofinance",
  account = "ecn",
) {
  const [data, setData] = useState<SpreadData[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  // Reconnect toast bookkeeping. We only toast on the FIRST true→true transition
  // after a real outage (>2s offline) so initial mount stays silent.
  const hasEverConnectedRef = useRef(false);
  const disconnectedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const mgr = WebSocketManager.getInstance();
    mgr.connect(broker, account);
    const onData = (next: SpreadData[]): void => setData(next);
    const onConn = (next: boolean): void => {
      setIsConnected((prev) => {
        if (prev === next) return prev;
        if (next) {
          if (hasEverConnectedRef.current) {
            const downFor = disconnectedAtRef.current
              ? Date.now() - disconnectedAtRef.current
              : 0;
            if (downFor > 2000) toast.success("Live feed restored");
          }
          hasEverConnectedRef.current = true;
          disconnectedAtRef.current = null;
        } else {
          disconnectedAtRef.current = Date.now();
        }
        return next;
      });
    };
    const unsub = mgr.subscribe(onData, onConn);
    return () => {
      unsub();
      mgr.disconnect();
    };
  }, [broker, account]);

  return { data, isConnected };
}
